import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Stack,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { Add, FilterList } from '@mui/icons-material'
import { TOUCH_MIN } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { TransporterPackageWithVehicles, TransporterGroup, Vehicle, VehicleNeeds as VehicleNeedsType } from '@otw/models'
interface SearchHeaderProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  searchPlaceholder?: string
  showSearch?: boolean
  showSoldToUserFilter?: boolean
  soldToUserFilter?: string
  onSoldToUserFilterChange?: (user: string) => void
  soldToUserOptions?: Array<{ value: string; label: string; count?: number }>
  showTransporterFilter?: boolean
  transporterFilter?: string
  onTransporterFilterChange?: (transporter: string) => void
  transporterOptions?: Array<{ value: string; label: string; count?: number }>
  showCurrentLocationFilter?: boolean
  currentLocationFilter?: string
  onCurrentLocationFilterChange?: (location: string) => void
  currentLocationOptions?: Array<{ value: string; label: string; count?: number }>
  showDropoffLocationFilter?: boolean
  dropoffLocationFilter?: string
  onDropoffLocationFilterChange?: (location: string) => void
  dropoffLocationOptions?: Array<{ value: string; label: string; count?: number }>
  showStatusFilter?: boolean
  statusFilter?: string
  onStatusFilterChange?: (status: string) => void
  statusFilterOptions?: Array<{ value: string; label: string; count?: number }>
  statusFilterLabel?: string
  showWorkStatusFilter?: boolean
  workStatusFilter?: string
  onWorkStatusFilterChange?: (workStatus: string) => void
  workStatusFilterOptions?: Array<{ value: string; label: string; count?: number }>
  showCreateButton?: boolean
  createButtonText?: string
  onCreateClick?: () => void
  showBulkUpdateButton?: boolean
  onBulkUpdateClick?: () => void
  showSecondaryButton?: boolean
  secondaryButtonText?: string
  onSecondaryClick?: () => void
  secondaryButtonVariant?: 'contained' | 'outlined'
  secondaryButtonIcon?: React.ReactNode
  showReadyForSaleButton?: boolean
  readyForSaleButtonText?: string
  onReadyForSaleClick?: () => void
  filterSummary?: string
  showMapLegend?: boolean
  showPackageNameFilter?: boolean
  packageNameFilter?: string
  onPackageNameFilterChange?: (packageName: string) => void
  packageNameOptions?: Array<{ value: string; label: string }>
  showDateFilter?: boolean
  dateFilter?: string
  onDateFilterChange?: (date: string) => void
  dateFilterOptions?: Array<{ value: string; label: string }>
  showActiveCompletedToggle?: boolean
  isActiveFilter?: boolean
  onActiveCompletedToggle?: (isActive: boolean) => void
  showTransportInventoryToggle?: boolean
  isTransportView?: boolean
  onTransportInventoryToggle?: (isTransport: boolean) => void
  transportInventoryToggleLabels?: { left: string; right: string }
  transportInventoryToggleWidth?: number
  showArchivedCheckbox?: boolean
  showArchived?: boolean
  onShowArchivedChange?: (showArchived: boolean) => void
  archivedCheckboxLabel?: string
  showFlaggedCheckbox?: boolean
  flaggedOnly?: boolean
  onFlaggedOnlyChange?: (flaggedOnly: boolean) => void
  flaggedCheckboxLabel?: string
  showSortBy?: boolean
  sortBy?: string
  onSortByChange?: (sortBy: string) => void
  sortByOptions?: Array<{ value: string; label: string }>
  sortByLabel?: string
  // Dashboard props
  showDashboard?: boolean
  packagesWithVehicles?: TransporterPackageWithVehicles[]
  transporterGroups?: TransporterGroup[]
  onDashboardActionClick?: (actionType: 'photos' | 'draft' | 'pickup' | 'overdue') => void
  onDashboardVehicleClick?: (vehicle: Vehicle, pkg: TransporterPackageWithVehicles, context?: { photoTab?: 'pickup' | 'postRepair' }) => void
  onDashboardPackageClick?: (pkg: TransporterPackageWithVehicles) => void
  onDashboardPickupDatesClick?: (pkg: TransporterPackageWithVehicles) => void
  onDashboardRepairDatesClick?: (pkg: TransporterPackageWithVehicles, dateType?: 'pickup' | 'repair') => void
  dashboardVariant?: 'admin' | 'repairOptic'
  getIsVehicleInCustomerPackage?: (vehicleId: string) => boolean
  onCreateCustomerPackage?: (
    vehicles: Vehicle[],
    pickupPrefill: { [vehicleId: string]: any },
    vehicleReady?: { [vehicleId: string]: { is_ready: boolean; ready_date: string } },
    interimPrefill?: { [vehicleId: string]: any },
    vehicleStatusPrefill?: { [vehicleId: string]: any }
  ) => void
  customDashboardContent?: React.ReactNode
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = "Search...",
  showSearch = true,
  showSoldToUserFilter = false,
  soldToUserFilter,
  onSoldToUserFilterChange,
  soldToUserOptions = [],
  showTransporterFilter = false,
  transporterFilter,
  onTransporterFilterChange,
  transporterOptions = [],
  showCurrentLocationFilter = false,
  currentLocationFilter,
  onCurrentLocationFilterChange,
  currentLocationOptions = [],
  showDropoffLocationFilter = false,
  dropoffLocationFilter,
  onDropoffLocationFilterChange,
  dropoffLocationOptions = [],
  showStatusFilter = false,
  statusFilter,
  onStatusFilterChange,
  statusFilterOptions = [],
  statusFilterLabel = 'Filter by Status',
  showWorkStatusFilter = false,
  workStatusFilter,
  onWorkStatusFilterChange,
  workStatusFilterOptions = [],
  showCreateButton = false,
  createButtonText = "Create",
  onCreateClick,
  showBulkUpdateButton = false,
  onBulkUpdateClick,
  showSecondaryButton = false,
  secondaryButtonText = "Secondary",
  onSecondaryClick,
  secondaryButtonVariant = 'outlined',
  secondaryButtonIcon = <Add />,
  showReadyForSaleButton = false,
  readyForSaleButtonText = "Ready for sale",
  onReadyForSaleClick,
  filterSummary,
  showMapLegend = false,
  showPackageNameFilter = false,
  packageNameFilter,
  onPackageNameFilterChange,
  packageNameOptions = [],
  showDateFilter = false,
  dateFilter,
  onDateFilterChange,
  dateFilterOptions = [],
  showActiveCompletedToggle = false,
  isActiveFilter = true,
  onActiveCompletedToggle,
  showTransportInventoryToggle = false,
  isTransportView = true,
  onTransportInventoryToggle,
  transportInventoryToggleLabels = { left: 'Transport', right: 'Inventory' },
  transportInventoryToggleWidth = 200,
  showArchivedCheckbox = false,
  showArchived = false,
  onShowArchivedChange,
  archivedCheckboxLabel = 'Archived',
  showFlaggedCheckbox = false,
  flaggedOnly = false,
  onFlaggedOnlyChange,
  flaggedCheckboxLabel = 'Flagged only',
  showSortBy = false,
  sortBy,
  onSortByChange,
  sortByOptions = [],
  sortByLabel = 'Sort by',
  // Dashboard props
  showDashboard = false,
  packagesWithVehicles = [],
  transporterGroups = [],
  onDashboardActionClick,
  onDashboardVehicleClick,
  onDashboardPackageClick,
  onDashboardPickupDatesClick,
  onDashboardRepairDatesClick,
  dashboardVariant,
  getIsVehicleInCustomerPackage,
  onCreateCustomerPackage,
  customDashboardContent,
}) => {
  const isMobile = useIsMobile()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const hasExtraFilters =
    showDateFilter ||
    showSoldToUserFilter ||
    showTransporterFilter ||
    showCurrentLocationFilter ||
    showDropoffLocationFilter ||
    showStatusFilter ||
    showWorkStatusFilter ||
    showArchivedCheckbox ||
    showFlaggedCheckbox ||
    showSortBy
  const showFilterCluster = !isMobile || filtersOpen

  const textFieldSx = {
    width: 300,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.paper',
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        }
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
          borderWidth: 2,
        }
      }
    }
  }

  const buttonSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    py: 1,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.2s ease-in-out'
  }

  const outlinedButtonSx = {
    ...buttonSx,
    borderColor: 'primary.main',
    color: 'primary.main',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'primary.50',
      borderColor: 'primary.dark',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  }

  return (
    <Box sx={{ 
      mb: 3,
      backgroundColor: 'background.default',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden'
    }}>
      {/* Main Search/Filter Controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { xs: 'flex-start', md: 'space-between' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 2, md: 0 },
        p: 2,
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3, 
          flexWrap: 'wrap',
          flex: 1
        }}>
          {showSearch && !showPackageNameFilter && (
            <TextField
              placeholder={searchPlaceholder}
              size='small'
              value={searchTerm}
              onChange={e => onSearchChange(e.target.value)}
              sx={{
                ...textFieldSx,
                width: { xs: '100%', sm: 300 },
                minWidth: { xs: 'auto', sm: 300 }
              }}
            />
          )}

          {showPackageNameFilter && onPackageNameFilterChange && (
            <Autocomplete
              size="small"
              options={packageNameOptions}
              value={packageNameOptions.find(option => option.value === packageNameFilter) || null}
              onChange={(_, newValue) => onPackageNameFilterChange(newValue?.value || '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search packages..."
                  sx={{
                    ...textFieldSx,
                    width: { xs: '100%', sm: 300 },
                    minWidth: { xs: 'auto', sm: 300 }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              )}
            />
          )}

          {isMobile && hasExtraFilters && (
            <Button
              variant={filtersOpen ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setFiltersOpen(open => !open)}
              sx={{
                minHeight: TOUCH_MIN,
                width: { xs: '100%', sm: 'auto' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {filtersOpen ? 'Hide filters' : 'Filters'}
            </Button>
          )}

          <Box sx={{ display: showFilterCluster ? 'contents' : 'none' }}>
          {showDateFilter && onDateFilterChange && (
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            >
              <InputLabel>Filter by Date</InputLabel>
              <Select
                value={dateFilter || 'ALL'}
                onChange={(e) => onDateFilterChange(e.target.value)}
                label="Filter by Date"
              >
                {dateFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Typography variant="body2">
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showSoldToUserFilter && onSoldToUserFilterChange && (
            <Autocomplete
              size="small"
              options={soldToUserOptions}
              value={soldToUserOptions.find(option => option.value === (soldToUserFilter || 'ALL')) || null}
              onChange={(_, newValue) => onSoldToUserFilterChange(newValue?.value || 'ALL')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              sx={{
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Customer"
                  placeholder="Customer"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.count !== undefined && option.value !== 'ALL' && (
                      <Typography variant="caption" color="text.secondary">
                        ({option.count})
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          )}

          {showTransporterFilter && onTransporterFilterChange && (
            <Autocomplete
              size="small"
              options={transporterOptions}
              value={transporterOptions.find(option => option.value === (transporterFilter || 'ALL')) || null}
              onChange={(_, newValue) => onTransporterFilterChange(newValue?.value || 'ALL')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              sx={{
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Transporter"
                  placeholder="Transporter"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.count !== undefined && option.value !== 'ALL' && (
                      <Typography variant="caption" color="text.secondary">
                        ({option.count})
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          )}

          {showCurrentLocationFilter && onCurrentLocationFilterChange && (
            <Autocomplete
              size="small"
              options={currentLocationOptions}
              value={currentLocationOptions.find(option => option.value === (currentLocationFilter || 'ALL')) || null}
              onChange={(_, newValue) => onCurrentLocationFilterChange(newValue?.value || 'ALL')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              sx={{
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Current Location"
                  placeholder="Current location"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.count !== undefined && option.value !== 'ALL' && (
                      <Typography variant="caption" color="text.secondary">
                        ({option.count})
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          )}

          {showDropoffLocationFilter && onDropoffLocationFilterChange && (
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            >
              <InputLabel>Filter by Repair Shop</InputLabel>
              <Select
                value={dropoffLocationFilter || 'ALL'}
                onChange={(e) => onDropoffLocationFilterChange(e.target.value)}
                label="Filter by Repair Shop"
              >
                {dropoffLocationOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                      {option.count !== undefined && option.value !== 'ALL' && (
                        <Typography variant="caption" color="text.secondary">
                          ({option.count})
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showStatusFilter && onStatusFilterChange && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            >
              <InputLabel>{statusFilterLabel}</InputLabel>
              <Select
                value={statusFilter || 'ALL'}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                label={statusFilterLabel}
              >
                {statusFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                      {option.count !== undefined && option.value !== 'ALL' && (
                        <Typography variant="caption" color="text.secondary">
                          ({option.count})
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showWorkStatusFilter && onWorkStatusFilterChange && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 225 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            >
              <InputLabel>Filter by Work status</InputLabel>
              <Select
                value={workStatusFilter || 'ALL'}
                onChange={(e) => onWorkStatusFilterChange(e.target.value)}
                label="Filter by Work status"
              >
                {workStatusFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                      {option.count !== undefined && option.value !== 'ALL' && (
                        <Typography variant="caption" color="text.secondary">
                          ({option.count})
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          </Box>

          {showActiveCompletedToggle && onActiveCompletedToggle && (
            <Box
              sx={{
                position: 'relative',
                width: 200,
                height: { xs: 44, sm: 32 },
                backgroundColor: 'background.subtle',
                borderRadius: 16,
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
              }}
              onClick={() => onActiveCompletedToggle(!isActiveFilter)}
            >
              {/* Sliding Pill Background */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: isActiveFilter ? '2px' : '50%',
                  width: 'calc(50% - 2px)',
                  height: 'calc(100% - 4px)',
                  backgroundColor: 'primary.main',
                  borderRadius: 14,
                  transition: 'all 0.3s ease',
                  zIndex: 1,
                }}
              />
              {/* Active Label */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isActiveFilter ? 'primary.contrastText' : 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Active
                </Typography>
              </Box>
              {/* Completed Label */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isActiveFilter ? 'text.secondary' : 'primary.contrastText',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Completed
                </Typography>
              </Box>
            </Box>
          )}

          {showTransportInventoryToggle && onTransportInventoryToggle && (
            <Box
              sx={{
                position: 'relative',
                width: transportInventoryToggleWidth,
                height: { xs: 44, sm: 32 },
                backgroundColor: 'background.subtle',
                borderRadius: 16,
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
              }}
              onClick={() => onTransportInventoryToggle(!isTransportView)}
            >
              {/* Sliding Pill Background */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: isTransportView ? '2px' : '50%',
                  width: 'calc(50% - 2px)',
                  height: 'calc(100% - 4px)',
                  backgroundColor: 'primary.main',
                  borderRadius: 14,
                  transition: 'all 0.3s ease',
                  zIndex: 1,
                }}
              />
              {/* Left Label */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isTransportView ? 'primary.contrastText' : 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {transportInventoryToggleLabels.left}
                </Typography>
              </Box>
              {/* Right Label */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isTransportView ? 'text.secondary' : 'primary.contrastText',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {transportInventoryToggleLabels.right}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: showFilterCluster ? 'contents' : 'none' }}>
          {showArchivedCheckbox && onShowArchivedChange && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showArchived}
                  onChange={(e) => onShowArchivedChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {archivedCheckboxLabel}
                </Typography>
              }
              sx={{ ml: 1 }}
            />
          )}
          {showFlaggedCheckbox && onFlaggedOnlyChange && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={flaggedOnly}
                  onChange={(e) => onFlaggedOnlyChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {flaggedCheckboxLabel}
                </Typography>
              }
              sx={{ ml: 1 }}
            />
          )}

          {showSortBy && onSortByChange && sortByOptions.length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 240 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <InputLabel>{sortByLabel}</InputLabel>
              <Select
                value={sortBy || sortByOptions[0]?.value || ''}
                onChange={(e) => onSortByChange(e.target.value)}
                label={sortByLabel}
              >
                {sortByOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Typography variant="body2">{option.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          {showMapLegend && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap',
              mr: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Legend:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'green',
                    border: '1px solid white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Pickup
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'red',
                    border: '1px solid white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Dropoff
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'black',
                    border: '1px solid white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Both
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 3,
                    backgroundColor: 'black',
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Route
                </Typography>
              </Box>
            </Box>
          )}
          
          {(showCreateButton ||
            showSecondaryButton ||
            showReadyForSaleButton ||
            (showBulkUpdateButton && onBulkUpdateClick)) && (
            <Stack direction='row' spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
              {showBulkUpdateButton && onBulkUpdateClick && (
                <Button
                  variant="outlined"
                  onClick={onBulkUpdateClick}
                  sx={{
                    ...outlinedButtonSx,
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  Bulk update
                </Button>
              )}
              {showSecondaryButton && onSecondaryClick && (
                <Button 
                  variant={secondaryButtonVariant}
                  startIcon={secondaryButtonIcon}
                  onClick={onSecondaryClick}
                  sx={{
                    ...(secondaryButtonVariant === 'outlined' ? outlinedButtonSx : buttonSx),
                    width: { xs: '100%', md: 'auto' }
                  }}
                >
                  {secondaryButtonText}
                </Button>
              )}
              {showReadyForSaleButton && onReadyForSaleClick && (
                <Button 
                  variant='contained' 
                  color='success'
                  onClick={onReadyForSaleClick}
                  sx={{
                    ...buttonSx,
                    width: { xs: '100%', md: 'auto' }
                  }}
                >
                  {readyForSaleButtonText}
                </Button>
              )}
              {showCreateButton && onCreateClick && (
                <Button 
                  variant='contained' 
                  startIcon={<Add />} 
                  onClick={onCreateClick}
                  sx={{
                    ...buttonSx,
                    width: { xs: '100%', md: 'auto' }
                  }}
                >
                  {createButtonText}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {customDashboardContent && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 0 }}>
          {customDashboardContent}
        </Box>
      )}

      {filterSummary && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {filterSummary}
          </Typography>
        </Box>
      )}

    </Box>
  )
}

export default SearchHeader
